/**
 * Calculation Verification UI — math/chemistry proof, not crop-target deviation.
 */

import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import type {
  CalculationVerificationResult,
  GateStatus,
} from '../../utils/recipeCalculationVerification';

function statusColor(
  s: GateStatus
): 'success' | 'error' | 'warning' | 'default' | 'info' {
  if (s === 'PASS') return 'success';
  if (s === 'FAIL') return 'error';
  if (s === 'WARNING' || s === 'CONDITIONAL') return 'warning';
  if (s === 'NOT_TESTED' || s === 'NOT_CALCULABLE') return 'default';
  return 'info';
}

interface Props {
  verification: CalculationVerificationResult;
  compact?: boolean;
}

const CalculationVerificationPanel: React.FC<Props> = ({
  verification: v,
  compact,
}) => {
  const overallColor =
    v.overallStatus === 'Calculation Verified'
      ? 'success'
      : v.overallStatus === 'FAILED'
        ? 'error'
        : 'warning';

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Calculation verification
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Independent proof of arithmetic &amp; chemistry (not crop-target fit)
            </Typography>
          </Box>
          <Chip
            label={v.overallStatus}
            color={overallColor}
            sx={{ fontWeight: 700 }}
          />
        </Stack>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip
            size="small"
            color={v.mathematicallyVerified ? 'success' : 'error'}
            label={
              v.mathematicallyVerified
                ? 'Mathematically verified'
                : 'Math verification failed'
            }
          />
          <Chip
            size="small"
            color={v.chemicallyValid ? 'success' : 'warning'}
            label={
              v.chemicallyValid ? 'Chemically valid' : 'Chemistry review needed'
            }
          />
          <Chip
            size="small"
            variant="outlined"
            label={`Working ${v.inputs.workingVolumeL} L · A ${v.inputs.stockVolumeAL} L @ 1:${v.inputs.injectionRatioA}`}
          />
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Dual path: direct final-volume ≡ stock g/L × injection fraction. Total N =
          NO₃-N + NH₄-N only (plain N never assumed to be one form).
        </Alert>

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Gate</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {v.gates.map((g) => (
                <TableRow key={g.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {g.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={g.status}
                      color={statusColor(g.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{g.detail}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Nitrogen */}
        <Alert
          severity={
            v.nitrogen.status === 'PASS'
              ? 'success'
              : v.nitrogen.status === 'FAIL'
                ? 'error'
                : 'warning'
          }
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" fontWeight={600}>
            Nitrogen forms
          </Typography>
          <Typography variant="body2">
            NO₃-N = {v.nitrogen.no3N.toFixed(2)} · NH₄-N ={' '}
            {v.nitrogen.nh4N.toFixed(2)} · Total N (forms) ={' '}
            {v.nitrogen.sumForms.toFixed(2)} mg/L
            {v.nitrogen.otherN > 0.05
              ? ` · plain N channel = ${v.nitrogen.otherN.toFixed(2)} mg/L`
              : ''}
          </Typography>
        </Alert>

        {/* Tank totals */}
        {v.tanks.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Tank dissolved solids
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {v.tanks.map((t) => (
                <Chip
                  key={t.tank}
                  label={`Tank ${t.tank}: ${t.totalMassG.toFixed(1)} g → ${t.stockConcentrationGL.toFixed(2)} g/L (${t.saltCount} salts)`}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {!compact && v.contributions.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Dual-path sample (first 12 salt × nutrient pairs)
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Salt</TableCell>
                    <TableCell>Tank</TableCell>
                    <TableCell>Element</TableCell>
                    <TableCell align="right">Route A mg/L</TableCell>
                    <TableCell align="right">Route B mg/L</TableCell>
                    <TableCell align="right">|Δ|</TableCell>
                    <TableCell align="right">Reverse mass err %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {v.contributions.slice(0, 12).map((c, i) => (
                    <TableRow key={i}>
                      <TableCell>{c.saltName}</TableCell>
                      <TableCell>{c.tank}</TableCell>
                      <TableCell>{c.normalized}</TableCell>
                      <TableCell align="right">{c.routeA_mgL.toFixed(3)}</TableCell>
                      <TableCell align="right">{c.routeB_mgL.toFixed(3)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={
                            c.dualPathDiff > 0.01 ? 'error.main' : 'success.main'
                          }
                        >
                          {c.dualPathDiff.toFixed(4)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {c.reverseMassDiffPct.toFixed(4)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CalculationVerificationPanel;
